import { createElement, type ComponentType, type CSSProperties } from 'react';
import { motion as framerMotion } from 'framer-motion';
import { usePublicMotionSettings } from './PublicMotionProvider';

type PublicMotionProps = Record<string, unknown>;
type PublicMotionComponent<Props extends object = PublicMotionProps> = ComponentType<Props>;
type PublicMotionTarget = string | ComponentType<object>;
type PublicMotionFactory = Record<string, PublicMotionComponent> & {
  create: <Props extends object>(target: ComponentType<Props>) => PublicMotionComponent<Props & PublicMotionProps>;
};
type PublicMotionStyle = CSSProperties & Record<string, string | number>;

const publicMotionCache = new Map<PublicMotionTarget, PublicMotionComponent>();
const PREP_CLASS_NAME = 'public-motion-prepare';
const MIN_VISIBLE_OPACITY = 0.9;
const MIN_VISIBLE_SCALE = 0.98;
const MAX_VISIBLE_BLUR_PX = 4;
const MAX_VISIBLE_OFFSET_PX = 16;

function resolveMotionState(source: unknown) {
  if (!source || typeof source !== 'object' || Array.isArray(source)) return null;

  return source as Record<string, unknown>;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function softenFilter(filter: string) {
  return filter.replace(/blur\(([\d.]+)px\)/g, (_match, blurAmount) => {
    const nextBlur = Math.min(Number(blurAmount), MAX_VISIBLE_BLUR_PX);
    return `blur(${nextBlur}px)`;
  });
}

function buildPreparedMotionState(state: Record<string, unknown>) {
  let changed = false;
  const nextState = { ...state };

  if (typeof nextState.opacity === 'number' && nextState.opacity < MIN_VISIBLE_OPACITY) {
    nextState.opacity = MIN_VISIBLE_OPACITY;
    changed = true;
  }

  if (typeof nextState.scale === 'number' && nextState.scale < MIN_VISIBLE_SCALE) {
    nextState.scale = MIN_VISIBLE_SCALE;
    changed = true;
  }

  if (typeof nextState.x === 'number') {
    const nextX = clamp(nextState.x, -MAX_VISIBLE_OFFSET_PX, MAX_VISIBLE_OFFSET_PX);
    if (nextX !== nextState.x) {
      nextState.x = nextX;
      changed = true;
    }
  }

  if (typeof nextState.y === 'number') {
    const nextY = clamp(nextState.y, -MAX_VISIBLE_OFFSET_PX, MAX_VISIBLE_OFFSET_PX);
    if (nextY !== nextState.y) {
      nextState.y = nextY;
      changed = true;
    }
  }

  if (typeof nextState.filter === 'string') {
    const softenedFilter = softenFilter(nextState.filter);
    if (softenedFilter !== nextState.filter) {
      nextState.filter = softenedFilter;
      changed = true;
    }
  }

  return changed ? nextState : state;
}

function resolvePreparedMotionState(initial: unknown, variants: unknown) {
  const initialState = resolveMotionState(initial);
  if (initialState) return buildPreparedMotionState(initialState);

  const variantMap = resolveMotionState(variants);
  if (!variantMap) return null;

  const hiddenState = resolveMotionState(variantMap.hidden);
  if (!hiddenState) return null;

  return buildPreparedMotionState(hiddenState);
}

function appendClassName(currentClassName: unknown, classNameToAppend: string) {
  return typeof currentClassName === 'string' && currentClassName.length > 0
    ? `${currentClassName} ${classNameToAppend}`
    : classNameToAppend;
}

function buildPreparedMotionStyle(
  currentStyle: unknown,
  preparedState: Record<string, unknown>,
): CSSProperties | undefined {
  const nextStyle = { ...(resolveMotionState(currentStyle) as CSSProperties | null) } as PublicMotionStyle;

  if (typeof preparedState.opacity === 'number') {
    nextStyle['--public-motion-opacity'] = String(preparedState.opacity);
  }

  if (typeof preparedState.x === 'number') {
    nextStyle['--public-motion-translate-x'] = `${preparedState.x}px`;
  }

  if (typeof preparedState.y === 'number') {
    nextStyle['--public-motion-translate-y'] = `${preparedState.y}px`;
  }

  if (typeof preparedState.scale === 'number') {
    nextStyle['--public-motion-scale'] = String(preparedState.scale);
  }

  if (typeof preparedState.filter === 'string') {
    nextStyle['--public-motion-filter'] = preparedState.filter;
  }

  return Object.keys(nextStyle).length > 0 ? nextStyle : undefined;
}

function buildPreparedMotionProps<Props extends object>(props: Props & PublicMotionProps) {
  const preparedState = resolvePreparedMotionState(props.initial, props.variants);
  if (!preparedState) return props;

  return {
    ...props,
    initial: false,
    className: appendClassName(props.className, PREP_CLASS_NAME),
    style: buildPreparedMotionStyle(props.style, preparedState),
  };
}

function resolveMotionComponent<Props extends object>(target: string | ComponentType<Props>) {
  if (typeof target === 'string') {
    return framerMotion[target as keyof typeof framerMotion] as PublicMotionComponent;
  }

  return framerMotion.create(target) as PublicMotionComponent<Props & PublicMotionProps>;
}

function buildPublicMotionComponent<Props extends object>(target: string | ComponentType<Props>) {
  const cacheKey = target as PublicMotionTarget;
  const cached = publicMotionCache.get(cacheKey) as PublicMotionComponent<Props & PublicMotionProps> | undefined;
  if (cached) return cached;

  const MotionComponent = resolveMotionComponent(target) as PublicMotionComponent<Props & PublicMotionProps>;
  const PreparedMotionComponent = (props: Props & PublicMotionProps) =>
    createElement(MotionComponent, buildPreparedMotionProps(props));

  const PublicMotionComponent = (props: Props & PublicMotionProps) => {
    const { ssrGateActive, suppressMountAnimations } = usePublicMotionSettings();

    if (ssrGateActive) return createElement(PreparedMotionComponent, props);

    // Al remount post-flip il contenuto SSR era già visibile: le animazioni di
    // mount (`animate`) non devono ripartire da hidden. Le animazioni da
    // scroll (`whileInView`) restano invariate.
    if (suppressMountAnimations && 'animate' in props) {
      return createElement(MotionComponent, { ...props, initial: false });
    }

    return createElement(MotionComponent, props);
  };

  PublicMotionComponent.displayName =
    typeof target === 'string'
      ? `PublicMotion(${target})`
      : `PublicMotion(${target.displayName || target.name || 'Component'})`;

  publicMotionCache.set(cacheKey, PublicMotionComponent as unknown as PublicMotionComponent);
  return PublicMotionComponent;
}

export const motion: PublicMotionFactory = new Proxy(
  {},
  {
    get(_target, prop) {
      if (prop === 'create') {
        return <Props extends object>(target: ComponentType<Props>) => buildPublicMotionComponent(target);
      }

      return buildPublicMotionComponent(prop as string);
    },
  },
) as PublicMotionFactory;
